import React, {useEffect, useState} from 'react';
import ContentBuilder from '../contentBuilderPage/ContentBuilder';
import NavMenu from './NavMenu';
import SettingsForm from '../settingsPage/POCToolSettingsForm';
import PreviewPage from '../previewPage/PreviewPage';
import {Box, Container, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {actionCreators} from '../../../../store/poctoolsContentManagement/poctoolsContentManagementActions';
import {getPOCToolById} from '../../../../api/POCWebPortalAPI/POCToolsCalls';
import {loadMiniToolsContent} from '../../../../Util/loader';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {convertingMiniToolsToDisplayPocToolContent} from '../../../../Util/converter';
import {createNewPOCTool} from '../helper/createPOCTool';
import {editPOCTool} from '../helper/editPOCTool';
import Loader from '../../../POCWebPortal/generalPurpose/Loader';
import AlertDialog from '../../generalPurpose/AlertDialog';

interface CreatePOCToolStagesProps {
  editMode?: boolean;
}

export const CreatePOCToolStages: React.FC<CreatePOCToolStagesProps> = (
  props: CreatePOCToolStagesProps,
) => {
  //This component handles navigation in between settings content builder and preview pages
  //Works similar to stack navigation

  const {editMode = false} = props;
  let {id} = useParams();
  id = id?.slice(1);

  const navigate = useNavigate();

  const [activePage, setActivePage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPOCToolChanged, setIsPOCToolChanged] = useState(false);
  const [isDialogAlertOpen, setIsDialogAlertOpen] = useState(false);

  const dispatch = useAppDispatch();
  const storePointOfCareTool = useAppSelector(
    state => state.poctoolsContentManagementState.pointOfCareTool,
  );
  const displayPocToolContent = useAppSelector(
    state => state.poctoolsContentManagementState.displayPocToolContent,
  );

  const onCreatePOCTool = POCToolCreatedId => {
    if (!POCToolCreatedId) {
      return alert('error with saving POCTool');
    }

    setTimeout(() => {
      navigate(`/POCtoolsCMSHome`);
      setIsSubmitted(false);
      setIsLoading(false);
      navigateStage(0);
    }, 1500);
  };

  const handleQuite = async () => {
    if (!isPOCToolChanged) {
      return navigate(`/POCtoolsCMSHome`);
    }
    setIsDialogAlertOpen(true);
  };

  const handleSave = async () => {
    if (
      storePointOfCareTool.name &&
      storePointOfCareTool.description &&
      (storePointOfCareTool.image.file || storePointOfCareTool.image.url)
    ) {
      try {
        if (
          editMode &&
          (!storePointOfCareTool.id || storePointOfCareTool.id == 'NewPOCTool')
        ) {
          //if it's new POCTool/SuperPOCTool
          return createNewPOCTool({
            pocToolSummary: storePointOfCareTool,
            displayPocToolContent: displayPocToolContent || [],
            setIsLoading,
            setIsSubmitted,
            navigate,
          }).then(onCreatePOCTool);
        } else {
          //update old POCTool/SuperPOCTool
          return editPOCTool({
            pocToolSummary: storePointOfCareTool,
            displayPocToolContent,
            id: storePointOfCareTool.id,
            setIsLoading,
            setIsSubmitted,
            navigate,
          }).then(onCreatePOCTool);
        }
      } catch (err) {
        if (err === 401) {
          navigate('/Login');
          throw err;
        }
      }
    } else {
      navigate(`/POCtoolsCMSHome`);
    }
  };

  const navigateStage = (index: number) => {
    setActivePage(index);
  };
  const goBackStage = () => {
    setActivePage(activePage - 1);
  };
  const goNextStage = () => {
    setActivePage(activePage + 1);
  };

  useEffect(() => {
    if (!editMode && !id && storePointOfCareTool.id !== 'NewPOCTool') {
      dispatch(actionCreators.ClearPOCTool());
    }
  }, [editMode, id]);

  useEffect(() => {
    if (
      editMode &&
      (storePointOfCareTool.id.length === 0 || storePointOfCareTool.id !== id)
    ) {
      getPOCToolById(id)
        .then(async poctool => {
          dispatch(
            actionCreators.setPOCToolSummary({
              id: poctool.id,
              name: poctool.name,
              description: poctool.description,
              image: {url: poctool.previewImageUrl},
            }),
          );
          if (poctool.isSuperPOCTool) {
            const minitoolsWithContent = await loadMiniToolsContent(poctool.id);
            const contentToDisplay =
              convertingMiniToolsToDisplayPocToolContent(minitoolsWithContent);
            dispatch(actionCreators.setPOCToolContent(contentToDisplay));
          } else {
            dispatch(
              actionCreators.setPOCToolContent(undefined, poctool.categories),
            );
          }
        })
        .catch(err => {
          console.log(err);
          alert('Network error');
        });
    }
  }, []);

  const navItems = [
    {label: 'POC Tool Settings'},
    {label: 'Content Builder'},
    {label: 'Preview'},
  ];

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Loader />
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container style={{marginTop: '25%'}} maxWidth="md">
        <Typography variant="h4" align="center">
          Your Point-of-Care Tool is saved!
        </Typography>
      </Container>
    );
  }

  return (
    <Box>
      <AlertDialog
        dialogTitle="Do you want to save before leaving?"
        dialogContentText="Your changes may not be saved if you proceed without saving."
        onYes={handleSave}
        onNo={() => {
          dispatch(actionCreators.ClearPOCTool());
          navigate(`/POCtoolsCMSHome`);
        }}
        isOpen={isDialogAlertOpen}
        setIsOpen={setIsDialogAlertOpen}
      />
      <Box paddingTop={3} paddingBottom={3}>
        <NavMenu
          activePage={activePage}
          navigateStage={navigateStage}
          navItems={navItems}
          handleQuite={handleQuite}
        />
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Box></Box>
        <Box style={styles.formContainer as React.CSSProperties}>
          {activePage === 0 && (
            <SettingsForm
              goNextStage={goNextStage}
              handleQuite={handleQuite}
              setIsPOCToolChanged={setIsPOCToolChanged}
              isPOCToolChanged={isPOCToolChanged}
            />
          )}
          {activePage === 1 && (
            <ContentBuilder
              goBackStage={goBackStage}
              goNextStage={goNextStage}
              isPOCToolChanged={isPOCToolChanged}
              setIsPOCToolChanged={setIsPOCToolChanged}
            />
          )}
          {activePage === 2 && <PreviewPage goBackStage={goBackStage} />}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  formContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '80%',
  },
  navContainer: {},
};
